import { AxiosResponse } from "axios";
import { parseApiData } from "go-core/api/parseApiData";
import { getSearchSelectPage, mapToBase } from "go-core/api/searchSelectService";
import { NEW_WAY_TO_ENCODING_FILTER_SIGN } from "go-list/core/components/Filter/services";
import { restApi } from "../../Security/api";
import {
	AdminOrganizationUserApi,
	CancelLoanFormProps,
	OrganizationPartnerInfoApi,
	TerminalApi,
} from "../Admin/adminTypes";
import { SubscriptionInvoiceApi, SubscriptionsApi } from "../Subscription/subscriptionTypes";
import {
	CreatePartnerOrganizationApi,
	LoanApi,
	OrganizationApi,
	OrganizationFeatureApi,
	OrganizationFormProps,
	OrganizationProductApi,
	OrganizationProductFormApi,
	OrganizationProductPerCategoryApi,
	OrganizationsDashboardApi,
	PayoutInvoiceApi,
	SearchSelectApi,
} from "../types";
import {
	PartnerApi,
	PartnerDashboardApi,
	PartnerFormProps,
	PartnerPayoutInvoiceFormProps,
	PartnerSearchSelectApi,
	PartnerSummaryApi,
	PartnerUserListApi,
	UserPermissions,
} from "./partnerTypes";

class partnerApi {
	static async getPartnersSearchSelect(search?: string): Promise<PartnerSearchSelectApi[]> {
		const query = btoa(unescape(encodeURIComponent(`${NEW_WAY_TO_ENCODING_FILTER_SIGN}query|c=${search}`)));
		const res: PartnerSearchSelectApi[] = await parseApiData(
			restApi.get(`admin/partners/search_select?f=${query}`)
		);
		return res.map((item) => {
			return {
				id: item.id,
				label: item.label,
				email: item.email,
				name: item.label,
			};
		});
	}

	static async getPartners(params?: Record<string, any>): Promise<PartnerApi[]> {
		return parseApiData(restApi.get(`/partners`, { params }));
	}

	static async getSubPartners(
		partnerId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<PartnerApi[]> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/sub_partners`, { params, cancelToken: options?.cancelToken })
		);
	}

	static async getSubPartnersSearchSelect(
		partnerId: number,
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>
	): Promise<AxiosResponse> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return restApi.get(`/partners/${partnerId}/sub_partners/search_select?f=${f}&page=${page}`, {
			cancelToken: options?.cancelToken,
		});
	}

	static async getPartner(id: number, params?: Record<string, any>): Promise<PartnerApi> {
		return parseApiData(restApi.get(`/partners/${id}`, { params }));
	}

	static async getPartnerDashboard(id: number, params?: Record<string, any>): Promise<PartnerDashboardApi> {
		return parseApiData(restApi.get(`/partners/${id}/dashboard`, { params }));
	}

	static async getPartnerReport(id?: number, params?: Record<string, any>, options?: Record<string, any>) {
		return restApi.get(`/partners/${id}/report`, { params, cancelToken: options?.cancelToken });
	}

	static async updatePartner(id: number, data: PartnerFormProps, params?: Record<string, any>): Promise<PartnerApi> {
		return parseApiData(restApi.put(`/partners/${id}`, data, { params }));
	}

	static async getPayoffs(
		id: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<PartnerSummaryApi[]> {
		return parseApiData(restApi.get(`/partners/${id}/summary`, { params, cancelToken: options?.cancelToken }));
	}

	static async getPayoff(id: number, summaryId: string): Promise<PartnerSummaryApi> {
		return parseApiData(restApi.get(`/partners/${id}/summary/${summaryId}`));
	}

	static async summarizePayoffs(id: number) {
		return restApi.put(`/partners/${id}/summarize`);
	}

	static async removeUserFromPartner(partnerId: number, email: string) {
		return restApi.delete(`partners/${partnerId}/users?email=${email}`);
	}

	static async getPartnerUsers(
		partnerId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<PartnerUserListApi[]> {
		return parseApiData(restApi.get(`/partners/${partnerId}/users`, { params, cancelToken: options?.cancelToken }));
	}

	static async checkOrganizationForLoan(organizationId: number): Promise<OrganizationApi> {
		return parseApiData(restApi.get(`/loans/organizations/${organizationId}`));
	}

	static async getPartnerOrganizationsSearchSelect(
		search: string,
		params: Record<string, any>,
		options?: Record<string, any>,
		partnerId?: number
	): Promise<SearchSelectApi[]> {
		const f = mapToBase(search, params);
		const page = getSearchSelectPage(params);
		return parseApiData(
			restApi.get(`partners/${partnerId}/organizations/search_select?f=${f}&page=${page}`, {
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getPartnerOrganization(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>
	): Promise<OrganizationApi> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/organizations/${organizationId}`, {
				params,
			})
		);
	}

	static async updateOrganization(
		partnerId: number,
		organizationId: number,
		data: OrganizationFormProps,
		params?: Record<string, any>
	): Promise<OrganizationApi> {
		return parseApiData(restApi.put(`/partners/${partnerId}/organizations/${organizationId}`, data, { params }));
	}

	static async getPartnerOrganizations(
		partnerId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationApi[]> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/organizations`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async addUserByPartner(partnerId: number, email: string): Promise<OrganizationApi> {
		return parseApiData(restApi.post(`/partners/${partnerId}/users?email=${encodeURIComponent(email)}`));
	}

	static updateUserPermissions(partnerId: number, email: string, newPermissions: UserPermissions[]) {
		return restApi.put(`/partners/${partnerId}/users/${email}`, { permissions: newPermissions });
	}

	static async createOrganizationByPartner(
		partnerId: number,
		data: CreatePartnerOrganizationApi
	): Promise<OrganizationApi> {
		return parseApiData(restApi.post(`/partners/${partnerId}/organizations`, data));
	}

	static async sendLoanViaEmail(partnerId: number, id: number, data: { email: string }) {
		return restApi.post(`/partners/${partnerId}/loans/${id}/pdf_via_mail`, data);
	}

	static async sendLoanEmailToClient(partnerId: number, id: number, data: { email: string }) {
		return restApi.post(`/partners/${partnerId}/loans/${id}/send_mail`, data);
	}

	static async updateLoan(partnerId: number, data: any, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/partners/${partnerId}/loans/${data.id}`, data, { params }));
	}

	static async createLoan(partnerId: number, data: LoanApi): Promise<LoanApi> {
		return parseApiData(restApi.post(`/partners/${partnerId}/loans`, data));
	}

	static async findLoanByReferenceId(
		partnerId: number,
		referenceId: string,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<LoanApi> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/loans/find_by_reference_id/${referenceId}`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getSummaryPdf(id: string) {
		return restApi.get(`/partners/summary/${id}/pdf`, { responseType: "blob" });
	}

	static async sendSummaryViaEmail(id: string, data: { email: string }) {
		return restApi.post(`/partners/summary/${id}/pdf_via_mail`, data);
	}

	static async getPayoutInvoices(
		id: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<PayoutInvoiceApi[]> {
		return parseApiData(
			restApi.get(`/partners/${id}/invoices`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async removePayoutInvoice(id: number, uid: string) {
		return restApi.delete(`/partners/${id}/invoices/${uid}`);
	}

	static async createPayoutInvoice(id: number, data: PartnerPayoutInvoiceFormProps) {
		return restApi.post(`/partners/${id}/invoices`, data);
	}

	static async getLoans(
		partnerId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<LoanApi[]> {
		return parseApiData(restApi.get(`/partners/${partnerId}/loans`, { params, cancelToken: options?.cancelToken }));
	}

	static async getLoan(partnerId: number, id: number, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.get(`/partners/${partnerId}/loans/${id}`, { params }));
	}

	static async getLoanPdf(partnerId: number, id: number) {
		return restApi.get(`/partners/${partnerId}/loans/${id}/pdf`, {
			responseType: "blob",
		});
	}

	static async openLoan(partnerId: number, id: number, params: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/partners/${partnerId}/loans/${id}/open`, undefined, { params }));
	}

	static async cancelLoan(
		partnerId: number,
		id: number,
		data: CancelLoanFormProps,
		params?: Record<string, any>
	): Promise<AxiosResponse> {
		return restApi.delete(`/partners/${partnerId}/loans/${id}`, { data, params });
	}

	static async acceptLoan(partnerId: number, id: number, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/partners/${partnerId}/loans/${id}/sign`, undefined, { params }));
	}

	static async cancelAcceptLoan(partnerId: number, id: number, params?: Record<string, any>): Promise<LoanApi> {
		return parseApiData(restApi.put(`/partners/${partnerId}/loans/${id}/cancel_sign`, undefined, { params }));
	}

	static async getOrganizationPartners(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>
	): Promise<OrganizationPartnerInfoApi[]> {
		return parseApiData(restApi.get(`/partners/${partnerId}/organizations/${organizationId}/partners`, { params }));
	}

	static async changeSubscriptionTrial(partnerId: number, id: number, data: { trial: boolean }) {
		return restApi.post(`/partners/${partnerId}/organizations/${id}/subscription/trial`, data);
	}

	static async getOrganizationSubscriptions(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>
	): Promise<SubscriptionsApi> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/organizations/${organizationId}/subscriptions`, { params })
		);
	}

	static async getOrganizationUsers(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>
	): Promise<AdminOrganizationUserApi[]> {
		return parseApiData(restApi.get(`/partners/${partnerId}/organizations/${organizationId}/users`, { params }));
	}

	static async getOrganizationsDashboard(partnerId: number): Promise<OrganizationsDashboardApi> {
		return parseApiData(restApi.get(`/partners/${partnerId}/organizations/dashboard`));
	}

	static async generateLoanTerminationFile(partnerId: number, loanId: number): Promise<AxiosResponse> {
		return restApi.get(`/partners/${partnerId}/loans/${loanId}/terminate/pdf`, {
			responseType: "blob",
		});
	}

	static async getTerminals(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<TerminalApi[]> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/organizations/${organizationId}/terminals`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrganizationInvoices(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<SubscriptionInvoiceApi[]> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/organizations/${organizationId}/invoices`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async resendInvitation(partnerId: number, organizationId: number, invitationId: number) {
		return restApi.post(`/partners/${partnerId}/organizations/${organizationId}/users/${invitationId}/resend`);
	}

	static async extendSubscriptionPayment(partnerId: number, id: number) {
		return restApi.post(`/partners/${partnerId}/organizations/${id}/extend_payment`);
	}

	static async getSubscriptionProformaPdf(
		partnerId: number,
		organizationId: number,
		proformaId: string
	): Promise<AxiosResponse> {
		return restApi.get(
			`/partners/${partnerId}/organizations/${organizationId}/invoices/proforma/${proformaId}/pdf`,
			{
				responseType: "blob",
			}
		);
	}

	static async getOrganizationProductsPerCategory(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationProductPerCategoryApi[]> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/organizations/${organizationId}/products_per_category`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async updateOrganizationProducts(
		partnerId: number,
		organizationId: number,
		data: OrganizationProductFormApi,
		params?: Record<string, any>
	): Promise<OrganizationProductApi[]> {
		return parseApiData(
			restApi.put(`/partners/${partnerId}/organizations/${organizationId}/products`, data, {
				params,
			})
		);
	}

	static async rewriteOrganizationProducts(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>
	): Promise<OrganizationApi> {
		return parseApiData(
			restApi.put(`/partners/${partnerId}/organizations/${organizationId}/update_products`, undefined, {
				params,
			})
		);
	}

	static async getOrganizationProducts(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationProductApi[]> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/organizations/${organizationId}/products`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async getOrganizationFeatures(
		partnerId: number,
		organizationId: number,
		params?: Record<string, any>,
		options?: Record<string, any>
	): Promise<OrganizationFeatureApi[]> {
		return parseApiData(
			restApi.get(`/partners/${partnerId}/organizations/${organizationId}/features`, {
				params,
				cancelToken: options?.cancelToken,
			})
		);
	}

	static async updateOrganizationFeatures(
		partnerId: number,
		organizationId: number,
		data: OrganizationProductFormApi,
		params?: Record<string, any>
	): Promise<OrganizationFeatureApi[]> {
		return parseApiData(
			restApi.put(`/partners/${partnerId}/organizations/${organizationId}/features`, data, {
				params,
			})
		);
	}
}

export { partnerApi };
